/**
 * Vessel Configuration Schema v1
 * Complete schema capturing ALL fields required by equipment gates
 * Stage 4A.7 - 100% Gate Coverage
 */

// Default vessel configuration with all required fields
export const defaultVesselConfig = {
  // Basic Information
  basic: {
    name: '',
    type: '', // 'pilot', 'passenger', 'crew_transfer', 'workboat', 'tug', 'survey', 'patrol'
    category: '', // 'cat0', 'cat1', 'cat2', 'cat3', 'cat4', 'cat5', 'cat6'
    operatingArea: '', // 'unrestricted', 'offshore', 'coastal', 'sheltered', 'inland'
    lengthOverall: 0, // meters
    grossTonnage: 0,
    operatingStatus: 'seagoing' // 'seagoing' | 'inland'
  },

  // Capacity
  capacity: {
    pob: 0, // Persons on board (total)
    pobChildren: 0,
    crewSize: 0,
    passengerCapacity: 0
  },

  // Machinery
  machinery: {
    engineType: '', // 'outboard', 'inboard_diesel', 'inboard_petrol', 'electric'
    engineLocation: '', // 'above_deck', 'below_deck', 'enclosed'
    enginePowerKW: 0,
    startMethod: '', // 'electric', 'manual', 'air', 'hydraulic'
    hasMachinerySpace: false,
    hasRudder: true,
    hasFuelSystem: true,
    hasExhaustLeakRisk: false
  },

  // Accommodation
  accommodation: {
    hasAccommodation: false,
    hasSleeping: false,
    hasCooking: false,
    hasHeating: false,
    hasToilet: false,
    hasOpenFlame: false,
    hasGasAppliance: false,
    hasLiquidFuelAppliance: false,
    hasLithiumBattery: false
  },

  // Operations
  operations: {
    operations: [], // Array of operation types
    hasCrane: false,
    hasLiftingDevice: false,
    hasDangerousGoods: false,
    hasRescueBoat: false,
    hasFastRescueBoat: false,
    personnelTransfer: false,
    towing: false,
    lifting: false,
    diving: false,
    bunkering: false,
    hasInternationalVoyage: false,
    hasImmersionSuits: false,
    hasInflatableLifejackets: false,
    hasOpenLiferaft: false,
    hasMultipleLiferafts: false,
    hasLineThrowing: false,
    hasDiveBasket: false,
    hasDiveLadder: false,
    hasDiverLift: false,
    hasGrabBag: false // Emergency grab bag
  },

  // Navigation & Communications
  navcomms: {
    seaArea: '', // 'A1', 'A2', 'A3', 'A4'
    hasECDIS: false,
    hasPaperCharts: true,
    hasPaperPubs: true,
    hasElectronicPubs: false,
    hasRadarReflector: false,
    hasGeneralAlarm: false,
    hasFixedVHF: false,
    hasPortableVHF: false,
    hasMF: false,
    hasROC: false,
    hasSART: false // Search and Rescue Transponder
  },

  // Environmental & Safety
  environmental: {
    operatingWaterTempC: 10,
    operatesInPDA: false, // Prohibited Discharge Area
    operatesInlandOnly: false,
    hasFireRiskArea: false,
    hasIMOSewageSystem: false,
    hasOilySludge: false,
    hasGasLeakRisk: false,
    hasElectricalSystem: true,
    hasConcentratedElectrical: false,
    hasFixedFireSystem: false,
    hasFirePump: false,
    hasEscapeRoute: false,
    hasSubstantialEnclosure: false,
    hasOpenSteeringPosition: false,
    // Additional electrical system tokens from GATE_TOKEN_STANDARDIZATION.md v1.5
    hasFixedRadio: false,
    hasElectricalNavEquipment: false,
    hasBackupBatteries: false,
    hasVentilatedBatteries: false,
    hasBatteryCharger: false,
    hasHazardousSpaces: false,
    hasHighVoltage: false,
    hasNightDeckOps: false,
    hasLightningRisk: false,
    // FFE-specific tokens
    isOpenBoat: false,
    hasFirePort: false,
    choosesFixed: false,
    dgNearAccommodation: false,
    hasMultipleExtinguishers: false,
    isPracticable: false,
    deemedNecessary: false,
    companyRequiresBuckets: false,
    // Administrative tokens
    isClass: '',
    isTransitioning: false,
    crewChangePercent: 0
  }
};

// Validation rules for required fields
export const requiredFields = {
  basic: ['name', 'type', 'category', 'lengthOverall'],
  capacity: ['pob', 'crewSize'],
  machinery: ['engineType', 'enginePowerKW'],
  accommodation: [], // All booleans with defaults
  operations: [], // All booleans with defaults
  navcomms: [], // Depends on operating area
  environmental: [] // All have defaults
};

// Progressive disclosure rules (when to show fields)
export const fieldVisibility = {
  'basic.category': (config) => config.basic.operatingStatus === 'seagoing',
  'capacity.passengerCapacity': (config) => config.basic.type === 'passenger',
  'capacity.pobChildren': (config) => config.basic.type === 'passenger',
  'machinery.engineLocation': (config) => config.machinery.engineType && config.machinery.engineType !== 'outboard',
  'machinery.hasMachinerySpace': (config) => config.machinery.engineType && config.machinery.engineType.includes('inboard'),
  'navcomms.seaArea': (config) => config.basic.operatingStatus === 'seagoing',
  'navcomms.hasMF': (config) => ['A2', 'A3', 'A4'].includes(config.navcomms.seaArea),
  'operations.hasDiveBasket': (config) => config.operations.diving === true,
  'operations.hasDiveLadder': (config) => config.operations.diving === true,
  'operations.hasDiverLift': (config) => config.operations.diving === true
};

// Field labels for UI
export const fieldLabels = {
  basic: {
    name: 'Vessel Name',
    type: 'Vessel Type',
    category: 'WBC3 Category',
    operatingArea: 'Operating Area',
    lengthOverall: 'Length Overall (m)',
    grossTonnage: 'Gross Tonnage',
    operatingStatus: 'Operating Status'
  },
  capacity: {
    pob: 'Total Persons on Board',
    pobChildren: 'Children on Board',
    crewSize: 'Crew Size',
    passengerCapacity: 'Passenger Capacity'
  },
  machinery: {
    engineType: 'Engine Type',
    engineLocation: 'Engine Location',
    enginePowerKW: 'Engine Power (kW)',
    startMethod: 'Start Method',
    hasMachinerySpace: 'Has Machinery Space',
    hasRudder: 'Has Rudder',
    hasFuelSystem: 'Has Fuel System',
    hasExhaustLeakRisk: 'Exhaust Leak Risk'
  },
  accommodation: {
    hasAccommodation: 'Has Accommodation',
    hasSleeping: 'Has Sleeping Areas',
    hasCooking: 'Has Cooking Facilities',
    hasHeating: 'Has Heating',
    hasToilet: 'Has Toilet',
    hasOpenFlame: 'Has Open Flame',
    hasGasAppliance: 'Has Gas Appliance',
    hasLiquidFuelAppliance: 'Has Liquid Fuel Appliance',
    hasLithiumBattery: 'Has Lithium Battery'
  },
  operations: {
    operations: 'Operation Types',
    hasCrane: 'Has Crane',
    hasLiftingDevice: 'Has Lifting Device',
    hasDangerousGoods: 'Carries Dangerous Goods',
    hasRescueBoat: 'Has Rescue Boat',
    hasFastRescueBoat: 'Has Fast Rescue Boat',
    personnelTransfer: 'Personnel Transfer',
    towing: 'Towing Operations',
    lifting: 'Lifting Operations',
    diving: 'Diving Operations',
    bunkering: 'Bunkering Operations'
  },
  navcomms: {
    seaArea: 'Sea Area',
    hasECDIS: 'Has ECDIS',
    hasPaperCharts: 'Has Paper Charts',
    hasRadarReflector: 'Has Radar Reflector',
    hasGeneralAlarm: 'Has General Alarm',
    hasFixedVHF: 'Has Fixed VHF',
    hasPortableVHF: 'Has Portable VHF',
    hasMF: 'Has MF Radio',
    hasROC: 'Has ROC Operator'
  },
  environmental: {
    operatingWaterTempC: 'Operating Water Temp (°C)',
    operatesInPDA: 'Operates in PDA',
    operatesInlandOnly: 'Inland Only',
    hasFireRiskArea: 'Has Fire Risk Area',
    hasIMOSewageSystem: 'Has IMO Sewage System',
    hasOilySludge: 'Has Oily Sludge',
    hasElectricalSystem: 'Has Electrical System',
    hasConcentratedElectrical: 'Has Concentrated Electrical',
    hasFixedFireSystem: 'Has Fixed Fire System',
    hasFirePump: 'Has Fire Pump',
    hasEscapeRoute: 'Has Escape Route',
    hasSubstantialEnclosure: 'Has Substantial Enclosure',
    hasOpenSteeringPosition: 'Has Open Steering Position',
    // Electrical system tokens
    hasFixedRadio: 'Has Fixed Radio Equipment',
    hasElectricalNavEquipment: 'Has Electrical Navigation Equipment',
    hasBackupBatteries: 'Has Backup Battery Systems',
    hasVentilatedBatteries: 'Has Ventilated Battery Types',
    hasBatteryCharger: 'Has Battery Charging System',
    hasHazardousSpaces: 'Has Explosion Risk Spaces',
    hasHighVoltage: 'Has High Voltage Systems',
    hasNightDeckOps: 'Conducts Night Deck Operations',
    hasLightningRisk: 'Operates in Lightning Risk Areas',
    // FFE-specific tokens
    isOpenBoat: 'Is Open Boat Type',
    hasFirePort: 'Has Fire Ports',
    choosesFixed: 'Chooses Fixed Fire System',
    dgNearAccommodation: 'DG Near Accommodation/Fuel/Machinery',
    hasMultipleExtinguishers: 'Has Multiple Fire Extinguishers',
    isPracticable: 'Fire Buckets Practicable',
    deemedNecessary: 'Fire Buckets Deemed Necessary',
    companyRequiresBuckets: 'Company Requires Fire Buckets',
    // Administrative tokens
    isClass: 'SOLAS Class',
    isTransitioning: 'Transitioning from Old Codes',
    crewChangePercent: 'Crew Change Percentage'
  }
};

// Options for select fields
export const fieldOptions = {
  'basic.type': [
    { value: 'pilot', label: 'Pilot Boat' },
    { value: 'passenger', label: 'Passenger Vessel' },
    { value: 'crew_transfer', label: 'Crew Transfer Vessel' },
    { value: 'workboat', label: 'Workboat' },
    { value: 'tug', label: 'Tug' },
    { value: 'survey', label: 'Survey Vessel' },
    { value: 'patrol', label: 'Patrol Vessel' }
  ],
  'basic.category': [
    { value: 'cat0', label: 'Category 0 - Unrestricted' },
    { value: 'cat1', label: 'Category 1 - Up to 20nm' },
    { value: 'cat2', label: 'Category 2 - Up to 5nm' },
    { value: 'cat3', label: 'Category 3 - Up to 2nm' },
    { value: 'cat4', label: 'Category 4 - Restricted local' },
    { value: 'cat5', label: 'Category 5 - Restricted local (simplified)' },
    { value: 'cat6', label: 'Category 6 - Recreational in commercial use' }
  ],
  'basic.operatingArea': [
    { value: 'unrestricted', label: 'Unrestricted' },
    { value: 'offshore', label: 'Offshore' },
    { value: 'coastal', label: 'Coastal' },
    { value: 'sheltered', label: 'Sheltered Waters' },
    { value: 'inland', label: 'Inland Waters' }
  ],
  'basic.operatingStatus': [
    { value: 'seagoing', label: 'Seagoing' },
    { value: 'inland', label: 'Inland' }
  ],
  'machinery.engineType': [
    { value: 'outboard', label: 'Outboard' },
    { value: 'inboard_diesel', label: 'Inboard Diesel' },
    { value: 'inboard_petrol', label: 'Inboard Petrol' },
    { value: 'electric', label: 'Electric' }
  ],
  'machinery.engineLocation': [
    { value: 'above_deck', label: 'Above Deck' },
    { value: 'below_deck', label: 'Below Deck' },
    { value: 'enclosed', label: 'Enclosed Space' }
  ],
  'machinery.startMethod': [
    { value: 'electric', label: 'Electric Starter' },
    { value: 'manual', label: 'Manual/Pull Start' },
    { value: 'air', label: 'Air Start' },
    { value: 'hydraulic', label: 'Hydraulic Start' }
  ],
  'navcomms.seaArea': [
    { value: 'A1', label: 'A1 - VHF coverage' },
    { value: 'A2', label: 'A2 - MF coverage' },
    { value: 'A3', label: 'A3 - Satellite coverage' },
    { value: 'A4', label: 'A4 - Polar regions' }
  ]
};

// Migration function for upgrading old configs
export function migrateVesselConfig(oldConfig) {
  // Start with defaults
  const newConfig = JSON.parse(JSON.stringify(defaultVesselConfig));
  
  if (!oldConfig) return newConfig;
  
  // Map old fields to new structure
  if (oldConfig.vesselName) newConfig.basic.name = oldConfig.vesselName;
  if (oldConfig.vesselType) newConfig.basic.type = oldConfig.vesselType;
  if (oldConfig.category) newConfig.basic.category = oldConfig.category;
  if (oldConfig.lengthOverall) newConfig.basic.lengthOverall = oldConfig.lengthOverall;
  if (oldConfig.tonnage) newConfig.basic.grossTonnage = oldConfig.tonnage;
  if (oldConfig.totalPersons) newConfig.capacity.pob = oldConfig.totalPersons;
  if (oldConfig.crewSize) newConfig.capacity.crewSize = oldConfig.crewSize;
  if (oldConfig.passengerCapacity) newConfig.capacity.passengerCapacity = oldConfig.passengerCapacity;
  if (oldConfig.engineType) newConfig.machinery.engineType = oldConfig.engineType;
  if (oldConfig.enginePower) newConfig.machinery.enginePowerKW = oldConfig.enginePower;
  
  // Map boolean fields
  const booleanMappings = {
    hasAccommodation: 'accommodation.hasAccommodation',
    hasSleeping: 'accommodation.hasSleeping',
    hasCooking: 'accommodation.hasCooking',
    hasHeating: 'accommodation.hasHeating',
    hasToilet: 'accommodation.hasToilet',
    hasCrane: 'operations.hasCrane',
    hasLiftingDevice: 'operations.hasLiftingDevice',
    hasDangerousGoods: 'operations.hasDangerousGoods',
    hasECDIS: 'navcomms.hasECDIS',
    hasPaperCharts: 'navcomms.hasPaperCharts'
  };
  
  Object.entries(booleanMappings).forEach(([oldKey, newPath]) => {
    if (oldConfig[oldKey] !== undefined) {
      const [section, field] = newPath.split('.');
      newConfig[section][field] = oldConfig[oldKey];
    }
  });
  
  return newConfig;
}

// Validate configuration completeness
export function validateVesselConfig(config) {
  const errors = [];
  const warnings = [];
  
  // Check required fields
  Object.entries(requiredFields).forEach(([section, fields]) => {
    fields.forEach(field => {
      if (!config[section][field]) {
        errors.push(`${fieldLabels[section][field]} is required`);
      }
    });
  });
  
  // Check logical consistency
  if (config.basic.type === 'passenger' && !config.capacity.passengerCapacity) {
    warnings.push('Passenger vessel should have passenger capacity specified');
  }
  
  if (config.machinery.engineType === 'outboard' && config.machinery.engineLocation) {
    warnings.push('Outboard engines do not have location specification');
  }
  
  if (config.basic.operatingStatus === 'inland' && config.navcomms.seaArea) {
    warnings.push('Inland vessels do not have sea areas');
  }
  
  return { errors, warnings, isValid: errors.length === 0 };
}

// Get percentage of fields completed
export function getCompletionPercentage(config) {
  let total = 0;
  let completed = 0;
  
  Object.entries(config).forEach(([section, fields]) => {
    Object.entries(fields).forEach(([field, value]) => {
      total++;
      if (value !== '' && value !== 0 && value !== false && (!Array.isArray(value) || value.length > 0)) {
        completed++;
      }
    });
  });
  
  return Math.round((completed / total) * 100);
}